import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  BookOpen, 
  Clock, 
  ArrowLeft, 
  ArrowRight,
  CheckCircle,
  Circle,
  Volume2,
  VolumeX,
  Pause,
  Play,
  SkipForward
} from 'lucide-react';
import { modulesApi, seedApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';

const Modules = () => {
  const [modules, setModules] = useState([]);
  const [myProgress, setMyProgress] = useState({});
  const [selectedModule, setSelectedModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioError, setAudioError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const audioRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadModules();
    
    // Cleanup on unmount
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Stop audio when module changes
  useEffect(() => {
    stopAudio();
  }, [selectedModule]);

  const loadModules = async () => {
    try {
      await seedApi.seed().catch(() => {});
      const [modulesRes, progressRes] = await Promise.all([
        modulesApi.getAll(),
        modulesApi.getMyProgress().catch(() => ({ data: [] })),
      ]);
      setModules(modulesRes.data || []);
      
      const progressMap = {};
      (progressRes.data || []).forEach(p => {
        progressMap[p.module_id] = p;
      });
      setMyProgress(progressMap);
    } catch (error) {
      console.error('Failed to load modules:', error);
      toast.error('Failed to load modules');
    } finally {
      setLoading(false);
    }
  };

  const getModuleProgress = (moduleId) => {
    return myProgress[moduleId]?.progress_percent || 0;
  };

  const isModuleCompleted = (moduleId) => {
    return myProgress[moduleId]?.completed || false;
  };

  const handleMarkComplete = async (moduleId) => {
    try {
      await modulesApi.updateProgress(moduleId, 100);
      setMyProgress(prev => ({
        ...prev,
        [moduleId]: { ...prev[moduleId], progress_percent: 100, completed: true }
      }));
      toast.success('Module completed!');
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      foundations: 'bg-[#26C6DA]/10 text-[#26C6DA]',
      'goal-setting': 'bg-[#26C6DA]/10 text-[#26C6DA]',
      execution: 'bg-emerald-500/20 text-emerald-400',
      frameworks: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400',
      leadership: 'bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400',
      'strategic-management': 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400',
      'lean-operating-system': 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400',
    };
    return colors[category] || 'bg-slate-100 text-slate-700 dark:bg-slate-700/30 dark:text-slate-300';
  };

  const formatCategoryName = (category) => {
    if (!category) return '';
    return category
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Audio functions using OpenAI TTS
  const startAudio = async () => {
    if (!selectedModule) return;
    
    setAudioLoading(true);
    setAudioError(null);
    
    try {
      // Get the audio URL from the API
      const audioUrl = modulesApi.getAudioUrl(selectedModule.id);
      const token = localStorage.getItem('token');
      
      // Create audio element with auth header via fetch
      const response = await fetch(audioUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to load audio');
      }
      
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      
      // Create and play audio
      const audio = new Audio(objectUrl);
      audioRef.current = audio;
      
      audio.onended = () => {
        setIsPlaying(false);
        setIsPaused(false);
        toast.success('Finished playing module audio');
        URL.revokeObjectURL(objectUrl);
      };
      
      audio.onerror = (e) => {
        console.error('Audio error:', e);
        setAudioError('Failed to play audio');
        setIsPlaying(false);
        setAudioLoading(false);
      };
      
      audio.oncanplaythrough = () => {
        setAudioLoading(false);
        audio.play();
        setIsPlaying(true);
        setIsPaused(false);
      };
      
    } catch (error) {
      console.error('Audio loading error:', error);
      setAudioError('Failed to generate audio. Please try again.');
      setAudioLoading(false);
      toast.error('Failed to load audio narration');
    }
  };

  const pauseAudio = () => {
    if (audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
      setIsPaused(true);
    }
  };

  const resumeAudio = () => {
    if (audioRef.current && audioRef.current.paused) {
      audioRef.current.play();
      setIsPaused(false);
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    setIsPlaying(false);
    setIsPaused(false);
    setAudioLoading(false);
  };

  const toggleAudio = () => {
    if (audioLoading) return;
    
    if (isPlaying) {
      if (isPaused) {
        resumeAudio();
      } else {
        pauseAudio();
      }
    } else {
      startAudio();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-slate-200 dark:border-[#1F2937] border-t-[#26C6DA] rounded-full animate-spin" />
      </div>
    );
  }

  if (selectedModule) {
    return (
      <div className="animate-fade-in-up" data-testid="module-detail">
        <button
          onClick={() => setSelectedModule(null)}
          className="inline-flex items-center gap-2 text-slate-500 dark:text-slate-400 hover:text-[#26C6DA] mb-6 transition-colors"
          data-testid="back-to-modules-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to modules
        </button>

        <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-3 ${getCategoryColor(selectedModule.category)}`}>
                {formatCategoryName(selectedModule.category)}
              </span>
              <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>
                {selectedModule.title}
              </h1>
              <div className="flex items-center gap-4 mt-2 text-slate-500 dark:text-slate-400">
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>{selectedModule.duration_minutes} min</span>
                </div>
              </div>
            </div>
            {isModuleCompleted(selectedModule.id) ? (
              <div className="flex items-center gap-2 text-emerald-600">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">Completed</span>
              </div>
            ) : (
              <Button
                onClick={() => handleMarkComplete(selectedModule.id)}
                className="bg-[#26C6DA] hover:bg-[#0891B2] text-white rounded-full"
                data-testid="mark-complete-btn"
              >
                Mark as Complete
              </Button>
            )}
          </div>

          {/* Voice Control Bar - Using OpenAI TTS */}
          <div className="flex items-center gap-4 mb-6 p-4 bg-slate-100 dark:bg-[#1A1A2E] rounded-xl border border-slate-200 dark:border-[#1F2937]" data-testid="voice-controls">
            <div className="flex items-center gap-2">
              <Button
                onClick={toggleAudio}
                variant="ghost"
                size="sm"
                disabled={audioLoading}
                className={`rounded-full w-10 h-10 p-0 ${isPlaying ? 'bg-[#26C6DA] text-white hover:bg-[#0891B2]' : 'bg-white hover:bg-[#26C6DA]/10'}`}
                data-testid="play-pause-btn"
              >
                {audioLoading ? (
                  <div className="w-5 h-5 border-2 border-slate-300 border-t-[#26C6DA] rounded-full animate-spin" />
                ) : isPlaying && !isPaused ? (
                  <Pause className="w-5 h-5" />
                ) : (
                  <Play className="w-5 h-5" />
                )}
              </Button>
              {isPlaying && (
                <Button
                  onClick={stopAudio}
                  variant="ghost"
                  size="sm"
                  className="rounded-full w-10 h-10 p-0 bg-white hover:bg-red-50 text-red-500"
                  data-testid="stop-btn"
                >
                  <VolumeX className="w-5 h-5" />
                </Button>
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                {audioLoading 
                  ? 'Generating audio...'
                  : isPlaying 
                    ? isPaused 
                      ? 'Paused' 
                      : 'Playing narration...' 
                    : 'Listen to this module'}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {audioLoading
                  ? 'First-time generation may take a moment'
                  : isPlaying 
                    ? 'Click pause to stop, or the X to end' 
                    : 'AI-powered narration with natural voice'}
              </p>
              {audioError && (
                <p className="text-xs text-red-500 mt-1">{audioError}</p>
              )}
            </div>
            {isPlaying && !isPaused && (
              <div className="flex items-center gap-2">
                <Volume2 className="w-4 h-4 text-[#26C6DA] animate-pulse" />
              </div>
            )}
          </div>

          <div className="prose prose-slate max-w-none markdown-content">
            {selectedModule.content.split('\n').map((line, i) => {
              if (line.startsWith('# ')) {
                return <h1 key={i} className="text-2xl font-bold mt-6 mb-4">{line.slice(2)}</h1>;
              }
              if (line.startsWith('## ')) {
                return <h2 key={i} className="text-xl font-semibold mt-5 mb-3">{line.slice(3)}</h2>;
              }
              if (line.startsWith('### ')) {
                return <h3 key={i} className="text-lg font-medium mt-4 mb-2">{line.slice(4)}</h3>;
              }
              if (line.startsWith('- ')) {
                return <li key={i} className="ml-4">{line.slice(2)}</li>;
              }
              if (line.startsWith('**') && line.endsWith('**')) {
                return <p key={i} className="font-semibold mb-2">{line.slice(2, -2)}</p>;
              }
              if (line.trim()) {
                return <p key={i} className="mb-3 leading-relaxed">{line}</p>;
              }
              return null;
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in-up" data-testid="modules-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-bold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>
          Learning Modules
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          Master strategic planning through structured, interactive lessons.
        </p>
      </div>

      {/* Progress Overview */}
      <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <span className="font-medium text-slate-800 dark:text-slate-100">Your Progress</span>
          <span className="text-sm text-slate-500 dark:text-slate-400">
            {Object.values(myProgress).filter(p => p.completed).length} of {modules.length} completed
          </span>
        </div>
        <Progress 
          value={(Object.values(myProgress).filter(p => p.completed).length / modules.length) * 100} 
          className="h-3 bg-[#EBE9E1]"
        />
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setSelectedCategory('all')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
            selectedCategory === 'all' 
              ? 'bg-[#26C6DA] text-white' 
              : 'bg-white dark:bg-[#111827] text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-[#1F2937] hover:border-[#26C6DA]'
          }`}
          data-testid="filter-all"
        >
          All ({modules.length})
        </button>
        {[...new Set(modules.map(m => m.category))].sort().map(category => (
          <button
            key={category}
            onClick={() => setSelectedCategory(category)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              selectedCategory === category 
                ? 'bg-[#26C6DA] text-white' 
                : 'bg-white dark:bg-[#111827] text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-[#1F2937] hover:border-[#26C6DA]'
            }`}
            data-testid={`filter-${category}`}
          >
            {formatCategoryName(category)} ({modules.filter(m => m.category === category).length})
          </button>
        ))}
      </div>

      {/* Modules Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {modules
          .filter(module => selectedCategory === 'all' || module.category === selectedCategory)
          .map((module, index) => (
          <div
            key={module.id}
            onClick={() => setSelectedModule(module)}
            className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-6 cursor-pointer hover:border-[#26C6DA]/50 hover:-translate-y-1 transition-all duration-300"
            data-testid={`module-card-${index}`}
          >
            <div className="flex items-start justify-between mb-4">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(module.category)}`}>
                {formatCategoryName(module.category)}
              </span>
              {isModuleCompleted(module.id) ? (
                <CheckCircle className="w-5 h-5 text-emerald-500" />
              ) : (
                <Circle className="w-5 h-5 text-slate-600 dark:text-slate-300" />
              )}
            </div>
            
            <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-2" style={{ fontFamily: 'Outfit' }}>
              {module.title}
            </h3>
            <p className="text-slate-500 dark:text-slate-400 text-sm mb-4 line-clamp-2">
              {module.description}
            </p>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1 text-slate-500 dark:text-slate-400 text-sm">
                <Clock className="w-4 h-4" />
                <span>{module.duration_minutes} min</span>
              </div>
              <div className="flex items-center gap-2">
                <Progress 
                  value={getModuleProgress(module.id)} 
                  className="w-20 h-2 bg-[#EBE9E1]"
                />
                <span className="text-xs text-slate-500 dark:text-slate-400">{getModuleProgress(module.id)}%</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Modules;
